import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number with appropriate suffix (B, M, K) and handle negative values
 */
export const formatReward = (reward: number) => {
  const absReward = Math.abs(parseFloat(reward.toString()));
  const sign = reward < 0 ? '-' : '';

  if (absReward >= 1000000000)
    return `${sign}${(absReward / 1000000000).toFixed(2)} B`;
  if (absReward >= 1000000)
    return `${sign}${(absReward / 1000000).toFixed(2)} M`;
  if (absReward > 10000) return `${sign}${(absReward / 1000).toFixed(4)} K`;

  return `${sign}${absReward.toFixed(8).replace(/\.?(0+)$/, '')}`;
};

/**
 * Format a date string to localized format
 */
export const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/**
 * Calculate quantity of coin based on investment amount and current price
 */
export const calculateCoinQuantity = (
  amount: number,
  currentPrice: number
): number => {
  if (currentPrice <= 0)
    throw new Error('Current price must be greater than 0');
  if (amount < 0) throw new Error('Amount must be non-negative');
  return amount / currentPrice;
};

/**
 * Generate buy button text with calculated coin quantity
 */
export const getBuyButtonText = (
  symbol: string,
  amount: number,
  currentPrice?: number
): string => {
  if (!currentPrice || currentPrice <= 0) return `Buy ${symbol}`;

  try {
    const coinQuantity = calculateCoinQuantity(amount, currentPrice);
    return `Buy ${formatReward(coinQuantity)} ${symbol} (${formatReward(amount)})`;
  } catch {
    return `Buy ${symbol}`;
  }
};

/**
 * Calculate sell quantity based on current quantity and percentage
 */
export const calculateSellQuantity = (
  currentQuantity: number,
  percentage: number
): number => {
  if (currentQuantity < 0)
    throw new Error('Current quantity must be non-negative');
  if (percentage < 0 || percentage > 100)
    throw new Error('Percentage must be between 0 and 100');
  return (currentQuantity * percentage) / 100;
};

/**
 * Generate sell button text with calculated sell quantity
 */
export const getSellButtonText = (
  symbol: string,
  currentQuantity: number,
  percentage: number
): string => {
  if (!symbol || percentage == null || isNaN(percentage) || percentage <= 0)
    return `Sell ${symbol}`;
  if (!currentQuantity || currentQuantity <= 0) return `Sell ${symbol}`;

  try {
    const sellQuantity = calculateSellQuantity(currentQuantity, percentage);
    return `Sell ${formatReward(sellQuantity)} ${symbol} (${percentage}%)`;
  } catch {
    return `Sell ${symbol}`;
  }
};
